import React, { Suspense, lazy, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';

import { LanguageProvider, useI18n } from './i18n/LanguageContext';
import { MediaProvider } from './site/MediaProvider';
import { CookieConsentProvider } from './site/CookieConsent';
import { Lang } from './types';

// Public landing stays eager (it's the prerendered, above-the-fold content).
import Landing from './pages/Landing';

// Legal pages and the whole admin dashboard are code-split so they never ship
// in the public landing bundle.
const LegalPage = lazy(() => import('./pages/legal/LegalPage'));
const CollectionPage = lazy(() => import('./pages/CollectionPage'));
const Login = lazy(() => import('./admin/Login'));
const AdminLayout = lazy(() => import('./admin/AdminLayout'));
const ProtectedRoute = lazy(() => import('./admin/ProtectedRoute'));
const ContentEditor = lazy(() => import('./admin/ContentEditor'));
const MediaManager = lazy(() => import('./admin/MediaManager'));
const LegalEditor = lazy(() => import('./admin/LegalEditor'));
const Messages = lazy(() => import('./admin/Messages'));
const ProductsAdmin = lazy(() => import('./admin/collections').then((m) => ({ default: m.ProductsAdmin })));
const ServicesAdmin = lazy(() => import('./admin/collections').then((m) => ({ default: m.ServicesAdmin })));
const CertificationsAdmin = lazy(() => import('./admin/collections').then((m) => ({ default: m.CertificationsAdmin })));
const LocationsAdmin = lazy(() => import('./admin/collections').then((m) => ({ default: m.LocationsAdmin })));

/** Scrolls to the top of the page on every route (pathname) change — so navigating
 *  from the footer/CTAs to /prodotti, /servizi or the legal pages lands at the top.
 *  Hash-only changes (#section anchors) don't change the pathname, so smooth-scroll still works. */
const ScrollToTop: React.FC = () => {
  const { pathname } = useLocation();
  useEffect(() => { window.scrollTo(0, 0); }, [pathname]);
  return null;
};

/** Forces a specific language for the lang-prefixed SEO routes (/de /en /it). */
const LangLanding: React.FC<{ lang: Lang }> = ({ lang }) => {
  const { setLang } = useI18n();
  useEffect(() => { setLang(lang); }, [lang, setLang]);
  return <Landing path={`/${lang}`} />;
};

const App: React.FC = () => (
  <HelmetProvider>
    <BrowserRouter>
      <LanguageProvider>
        <MediaProvider>
          <CookieConsentProvider>
            <ScrollToTop />
            <Suspense fallback={null}>
            <Routes>
              <Route path="/" element={<Landing path="/" />} />
              <Route path="/de" element={<LangLanding lang="de" />} />
              <Route path="/en" element={<LangLanding lang="en" />} />
              <Route path="/it" element={<LangLanding lang="it" />} />

              <Route path="/prodotti" element={<CollectionPage kind="products" />} />
              <Route path="/servizi" element={<CollectionPage kind="services" />} />

              <Route path="/privacy-policy" element={<LegalPage docKey="privacy" path="/privacy-policy" />} />
              <Route path="/terms-and-conditions" element={<LegalPage docKey="terms" path="/terms-and-conditions" />} />
              <Route path="/cookie-policy" element={<LegalPage docKey="cookie" path="/cookie-policy" />} />

              <Route path="/admin/login" element={<Login />} />
              <Route path="/admin" element={<Navigate to="/admin/content" replace />} />
              <Route
                element={<ProtectedRoute><AdminLayout /></ProtectedRoute>}
              >
                <Route path="/admin/content" element={<ContentEditor />} />
                <Route path="/admin/products" element={<ProductsAdmin />} />
                <Route path="/admin/services" element={<ServicesAdmin />} />
                <Route path="/admin/certifications" element={<CertificationsAdmin />} />
                <Route path="/admin/locations" element={<LocationsAdmin />} />
                <Route path="/admin/media" element={<MediaManager />} />
                <Route path="/admin/legal" element={<LegalEditor />} />
                <Route path="/admin/messages" element={<Messages />} />
              </Route>

              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
            </Suspense>
          </CookieConsentProvider>
        </MediaProvider>
      </LanguageProvider>
    </BrowserRouter>
  </HelmetProvider>
);

export default App;
